/**
 * Ceballos Total Framing - Luxury Design System
 * Premium design tokens and component specifications for high-end development
 * Designed for Arizona's most prestigious custom homes and commercial projects
 */

// Primary Brand Identity - Premium Orange & Steel
export const brandColors = {
  // Signature Orange - Ceballos Brand
  ceballos: {
    50: '#FFF8F1',
    100: '#FFEEE0',
    200: '#FFD4B8',
    300: '#FFB896',
    400: '#FF9C74',
    500: '#F56500', // Primary brand orange
    600: '#E55A00',
    700: '#CC5100',
    800: '#B34700',
    900: '#993D00'
  },
  
  // Steel Sophistication - Professional Authority
  steel: {
    50: '#FAFBFC',
    100: '#F4F6F8',
    200: '#E6EAED',
    300: '#D8DDE2',
    400: '#C4CDD5',
    500: '#8B96A5', // Primary steel
    600: '#6B7684',
    700: '#4A5568', // Deep professional steel
    800: '#2D3748', // Primary dark steel
    900: '#1A202C'  // Deepest steel
  }
};

// Comprehensive Color System
export const colors = {
  // Brand Colors
  primary: brandColors.ceballos,
  secondary: brandColors.steel,
  
  // Neutral Sophistication
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    950: '#0F0F0F'
  },
  
  // Luxury Accents
  gold: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#D69E2E', // Luxury gold
    600: '#B45309',
    700: '#92400E',
    800: '#78350F',
    900: '#451A03'
  },
  
  // Professional Status Colors
  success: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E',
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D'
  },
  
  warning: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F'
  },
  
  error: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D'
  },
  
  // Luxury Material Colors
  platinum: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A'
  }
};

// Premium Spacing System - Based on 4px grid for pixel-perfect results
export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  11: '2.75rem',    // 44px
  12: '3rem',       // 48px
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
  28: '7rem',       // 112px
  32: '8rem',       // 128px
  36: '9rem',       // 144px
  40: '10rem',      // 160px
  44: '11rem',      // 176px
  48: '12rem',      // 192px
  52: '13rem',      // 208px
  56: '14rem',      // 224px
  60: '15rem',      // 240px
  64: '16rem',      // 256px
  72: '18rem',      // 288px
  80: '20rem',      // 320px
  96: '24rem'       // 384px
};

// Professional Typography System
export const typography = {
  fontFamily: {
    sans: [
      'Inter', 
      '-apple-system', 
      'BlinkMacSystemFont', 
      'Segoe UI', 
      'Roboto', 
      'Helvetica Neue', 
      'Arial', 
      'sans-serif'
    ],
    serif: [
      'Playfair Display',
      'Georgia', 
      'Times New Roman', 
      'serif'
    ],
    mono: [
      'JetBrains Mono',
      'SF Mono', 
      'Monaco', 
      'Inconsolata', 
      'Roboto Mono', 
      'monospace'
    ],
    display: [
      'Inter',
      'system-ui',
      'sans-serif'
    ]
  },
  
  fontSize: {
    xs: ['0.75rem', { lineHeight: '1rem' }],      // 12px
    sm: ['0.875rem', { lineHeight: '1.25rem' }],  // 14px
    base: ['1rem', { lineHeight: '1.5rem' }],     // 16px
    lg: ['1.125rem', { lineHeight: '1.75rem' }],  // 18px
    xl: ['1.25rem', { lineHeight: '1.75rem' }],   // 20px
    '2xl': ['1.5rem', { lineHeight: '2rem' }],    // 24px
    '3xl': ['1.875rem', { lineHeight: '2.25rem' }], // 30px
    '4xl': ['2.25rem', { lineHeight: '2.5rem' }], // 36px
    '5xl': ['3rem', { lineHeight: '1' }],         // 48px
    '6xl': ['3.75rem', { lineHeight: '1' }],      // 60px
    '7xl': ['4.5rem', { lineHeight: '1' }],       // 72px
    '8xl': ['6rem', { lineHeight: '1' }],         // 96px
    '9xl': ['8rem', { lineHeight: '1' }]          // 128px
  },
  
  fontWeight: {
    thin: '100',
    extralight: '200',
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900'
  },
  
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
    luxury: '0.02em'
  },
  
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
    luxury: '1.6'
  }
};

// Luxury Shadow System
export const shadows = {
  // Standard Shadows
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
  
  // Luxury Premium Shadows
  luxury: '0 32px 64px -12px rgb(0 0 0 / 0.25), 0 16px 32px -16px rgb(0 0 0 / 0.1)',
  premium: '0 40px 80px -16px rgb(0 0 0 / 0.3), 0 20px 40px -20px rgb(0 0 0 / 0.15)',
  
  // Brand-specific Shadows
  ceballos: '0 25px 50px -12px rgb(245 101 0 / 0.25)',
  steel: '0 25px 50px -12px rgb(45 55 72 / 0.25)',
  
  // Glow Effects
  glow: '0 0 20px rgb(245 101 0 / 0.3)',
  glowSoft: '0 0 40px rgb(245 101 0 / 0.15)',
  
  // Architectural Shadows
  architectural: '0 50px 100px -20px rgb(0 0 0 / 0.25), 0 30px 60px -30px rgb(0 0 0 / 0.3)'
};

// Professional Border Radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  '3xl': '1.5rem',  // 24px
  '4xl': '2rem',    // 32px
  '5xl': '2.5rem',  // 40px
  full: '9999px',
  
  // Luxury specific
  luxury: '1.25rem', // 20px
  premium: '1.75rem' // 28px
};

// Luxury Gradients
export const gradients = {
  // Primary Brand Gradients
  ceballos: 'linear-gradient(135deg, #F56500 0%, #FF9C74 100%)',
  ceballosReverse: 'linear-gradient(135deg, #FF9C74 0%, #F56500 100%)',
  ceballosRadial: 'radial-gradient(circle at center, #F56500 0%, #CC5100 100%)',
  
  // Steel Professional
  steel: 'linear-gradient(135deg, #4A5568 0%, #8B96A5 100%)',
  steelReverse: 'linear-gradient(135deg, #8B96A5 0%, #4A5568 100%)',
  steelDark: 'linear-gradient(135deg, #2D3748 0%, #4A5568 100%)',
  
  // Luxury Combinations
  luxury: 'linear-gradient(135deg, #1A202C 0%, #4A5568 50%, #F56500 100%)',
  premiumSteel: 'linear-gradient(135deg, #1A202C 0%, #2D3748 50%, #4A5568 100%)',
  
  // Soft Backgrounds
  ceballosSoft: 'linear-gradient(135deg, #FFF8F1 0%, #FFEEE0 100%)',
  steelSoft: 'linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)',
  
  // Architectural
  architectural: 'linear-gradient(145deg, #0F172A 0%, #1A202C 25%, #2D3748 75%, #4A5568 100%)',
  
  // Overlay Gradients
  overlay: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
  overlayRadial: 'radial-gradient(circle at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)'
};

// Professional Animation System
export const animations = {
  // Timing Functions
  timing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Luxury Custom Curves
    luxury: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    premium: 'cubic-bezier(0.165, 0.84, 0.44, 1)',
    architectural: 'cubic-bezier(0.77, 0, 0.175, 1)',
    sophisticated: 'cubic-bezier(0.19, 1, 0.22, 1)'
  },
  
  // Duration Scale
  duration: {
    fastest: '100ms',
    fast: '200ms',
    normal: '300ms',
    slow: '500ms',
    slower: '700ms',
    slowest: '1000ms',
    
    // Luxury Timing
    luxuryFast: '400ms',
    luxuryNormal: '600ms',
    luxurySlow: '800ms'
  },
  
  // Keyframes
  keyframes: {
    fadeIn: {
      '0%': { opacity: '0', transform: 'translateY(20px)' },
      '100%': { opacity: '1', transform: 'translateY(0)' }
    },
    
    slideInLeft: {
      '0%': { opacity: '0', transform: 'translateX(-40px)' },
      '100%': { opacity: '1', transform: 'translateX(0)' }
    },
    
    scaleIn: {
      '0%': { opacity: '0', transform: 'scale(0.95)' },
      '100%': { opacity: '1', transform: 'scale(1)' }
    },
    
    luxuryReveal: {
      '0%': { 
        opacity: '0', 
        transform: 'translateY(40px) scale(0.95)',
        filter: 'blur(4px)'
      },
      '100%': { 
        opacity: '1', 
        transform: 'translateY(0) scale(1)',
        filter: 'blur(0px)'
      }
    }
  }
};

// Component Style System
export const componentStyles = {
  // Card Components
  card: {
    base: 'bg-white rounded-3xl border border-neutral-100',
    shadow: 'shadow-xl',
    hover: 'hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500',
    interactive: 'transform hover:scale-105 cursor-pointer',
    luxury: 'bg-white rounded-3xl shadow-luxury border border-neutral-100/50 backdrop-blur-sm'
  },
  
  // Button System
  button: {
    // Primary Buttons
    primary: [
      'inline-flex items-center justify-center',
      'bg-primary-500 hover:bg-primary-600',
      'text-white font-semibold',
      'px-8 py-4 rounded-xl',
      'transition-all duration-300 ease-luxury',
      'transform hover:scale-105',
      'shadow-lg hover:shadow-xl hover:shadow-primary-500/25',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed'
    ].join(' '),
    
    // Secondary Buttons
    secondary: [
      'inline-flex items-center justify-center',
      'border-2 border-primary-500 hover:bg-primary-500',
      'text-primary-500 hover:text-white font-semibold',
      'px-8 py-4 rounded-xl',
      'transition-all duration-300 ease-luxury',
      'transform hover:scale-105',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
    ].join(' '),
    
    // Luxury Buttons
    luxury: [
      'inline-flex items-center justify-center',
      'bg-gradient-to-r from-primary-500 to-primary-600',
      'text-white font-semibold',
      'px-12 py-5 rounded-2xl',
      'transition-all duration-500 ease-luxury',
      'transform hover:scale-105',
      'shadow-luxury hover:shadow-premium hover:shadow-primary-500/30',
      'relative overflow-hidden group',
      'before:absolute before:inset-0 before:bg-gradient-to-r before:from-primary-600 before:to-primary-700',
      'before:transform before:scale-x-0 before:group-hover:scale-x-100',
      'before:transition-transform before:duration-500 before:origin-left',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
    ].join(' ')
  },
  
  // Layout Containers
  container: {
    base: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    narrow: 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8',
    wide: 'max-w-8xl mx-auto px-4 sm:px-6 lg:px-8',
    full: 'w-full px-4 sm:px-6 lg:px-8'
  },
  
  // Section Spacing
  section: {
    hero: 'pt-72 pb-24',
    base: 'py-24',
    compact: 'py-16',
    spacious: 'py-32',
    luxury: 'py-40'
  },
  
  // Text Styles
  text: {
    hero: 'text-5xl md:text-7xl font-light leading-tight tracking-wide',
    heading: 'text-4xl md:text-5xl font-light leading-tight',
    subheading: 'text-2xl md:text-3xl font-light leading-relaxed',
    body: 'text-lg leading-relaxed',
    caption: 'text-sm text-neutral-600 leading-normal'
  },
  
  // Form Components
  form: {
    input: [
      'w-full px-4 py-4',
      'bg-white border border-neutral-300 rounded-xl',
      'text-neutral-900 placeholder-neutral-500',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
      'transition-all duration-300 ease-luxury'
    ].join(' '),
    
    textarea: [
      'w-full px-4 py-4',
      'bg-white border border-neutral-300 rounded-xl',
      'text-neutral-900 placeholder-neutral-500',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
      'transition-all duration-300 ease-luxury',
      'resize-none'
    ].join(' '),
    
    select: [
      'w-full px-4 py-4',
      'bg-white border border-neutral-300 rounded-xl',
      'text-neutral-900',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
      'transition-all duration-300 ease-luxury',
      'appearance-none bg-no-repeat bg-right'
    ].join(' ')
  }
};

// Responsive Breakpoints
export const breakpoints = {
  xs: '480px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
  '3xl': '1920px'
};

// Z-Index Scale
export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800
};

// Export complete design system
export default {
  colors,
  brandColors,
  spacing,
  typography,
  shadows,
  borderRadius,
  gradients,
  animations,
  componentStyles,
  breakpoints,
  zIndex
};

// Utility function to generate CSS custom properties
export const generateCSSVariables = () => {
  const cssVars = {};
  
  // Generate color variables
  Object.entries(colors).forEach(([colorName, colorShades]) => {
    if (typeof colorShades === 'object') {
      Object.entries(colorShades).forEach(([shade, value]) => {
        cssVars[`--color-${colorName}-${shade}`] = value;
      });
    }
  });
  
  // Generate spacing variables
  Object.entries(spacing).forEach(([size, value]) => {
    cssVars[`--spacing-${size}`] = value;
  });
  
  return cssVars;
};

// Theme configuration for luxury branding
export const luxuryTheme = {
  colors: {
    primary: brandColors.ceballos[500],
    secondary: brandColors.steel[700],
    accent: colors.gold[500],
    background: colors.neutral[50],
    surface: colors.neutral[100],
    text: colors.neutral[900]
  },
  typography: {
    headingFont: typography.fontFamily.display,
    bodyFont: typography.fontFamily.sans,
    scale: 'luxury'
  },
  effects: {
    primaryShadow: shadows.ceballos,
    hoverShadow: shadows.luxury,
    borderRadius: borderRadius.luxury
  }
};